import * as React from "react"
import { WorkItemType } from "@digits-graphql/frontend/graphql-bearer"
import useRouter from "@digits-shared/hooks/useRouter"
import {
  SelectedWorkItemState,
  WorkItemQueryParam,
} from "src/frontend/components/OS/Applications/Boost/WorkItems/hooks/boostTypes"
import { useFrontendPathGenerator } from "src/frontend/hooks/useFrontendPathGenerator"
import routes from "src/frontend/routes"

type WorkItemContextProps = {
  selectedWorkItemId?: string
  setSelectedWorkItemId: (workItemId?: string) => void
  selectedWorkItemType?: WorkItemType
  setSelectedWorkItemType: (workItemType?: WorkItemType) => void
  selectedWorkItemState: SelectedWorkItemState
  setSelectedWorkItemState: (workItemState: SelectedWorkItemState) => void
  isOpen: boolean
  isClosed: boolean
  hasAnimated: boolean
  setHasAnimated: (hasAnimated: boolean) => void
}

const WorkItemsContext = React.createContext<WorkItemContextProps>({
  setSelectedWorkItemId: () => {},
  setSelectedWorkItemType: () => {},

  selectedWorkItemState: SelectedWorkItemState.Open,
  setSelectedWorkItemState: () => {},

  isOpen: true,
  isClosed: false,
  hasAnimated: false,
  setHasAnimated: () => {},
})

export const WorkItemsContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { selectedWorkItemId, setSelectedWorkItemId } = useSelectedWorkItemId()
  const { selectedWorkItemType, setSelectedWorkItemType } = useSelectedWorkItemType()
  const { selectedWorkItemState, setSelectedWorkItemState } = useSelectedWorkItemState()
  // Should only animate (highlight the selected row) if there is a selected work item id on initial load
  // hasAnimated is used to prevent the animation from running on subsequent changes
  const [hasAnimated, setHasAnimated] = React.useState(!selectedWorkItemId)
  const isOpen = selectedWorkItemState === SelectedWorkItemState.Open
  const isClosed = selectedWorkItemState === SelectedWorkItemState.Closed

  const context = React.useMemo(
    () => ({
      selectedWorkItemId,
      setSelectedWorkItemId,
      selectedWorkItemType,
      setSelectedWorkItemType,
      selectedWorkItemState,
      setSelectedWorkItemState,
      isOpen,
      isClosed,
      hasAnimated,
      setHasAnimated,
    }),
    [
      selectedWorkItemId,
      setSelectedWorkItemId,
      selectedWorkItemType,
      setSelectedWorkItemType,
      selectedWorkItemState,
      setSelectedWorkItemState,
      isOpen,
      isClosed,
      hasAnimated,
      setHasAnimated,
    ]
  )

  return <WorkItemsContext.Provider value={context}>{children}</WorkItemsContext.Provider>
}

export function useWorkItemsContext() {
  const context = React.useContext(WorkItemsContext)
  return React.useMemo(() => context, [context])
}

// TODO: Maybe use DisplayOption for these?
function useSelectedWorkItemType() {
  const { location } = useRouter()
  const setQueryParam = useSetWorkItemQueryParam()

  const selectedWorkItemType = React.useMemo<WorkItemType | undefined>(() => {
    const value = location.queryParams[WorkItemQueryParam.Type] as WorkItemType
    return WorkItemType[value] || undefined
  }, [location.queryParams])

  const setSelectedWorkItemType = React.useCallback(
    (newType?: WorkItemType) => {
      setQueryParam(WorkItemQueryParam.Type, newType)
    },
    [setQueryParam]
  )

  return { selectedWorkItemType, setSelectedWorkItemType }
}

function useSelectedWorkItemState() {
  const { location } = useRouter()
  const setQueryParam = useSetWorkItemQueryParam()

  const selectedWorkItemState = React.useMemo<SelectedWorkItemState>(() => {
    const value = location.queryParams[WorkItemQueryParam.State] as SelectedWorkItemState
    return SelectedWorkItemState[value] || SelectedWorkItemState.Open
  }, [location.queryParams])

  const setSelectedWorkItemState = React.useCallback(
    (newState?: SelectedWorkItemState) => {
      setQueryParam(
        WorkItemQueryParam.State,
        newState === SelectedWorkItemState.Open ? undefined : newState
      )
    },
    [setQueryParam]
  )

  return { selectedWorkItemState, setSelectedWorkItemState }
}

function useSelectedWorkItemId() {
  const { location } = useRouter()
  const setQueryParam = useSetWorkItemQueryParam()

  const setSelectedWorkItemId = React.useCallback(
    (workItemId?: string) => {
      setQueryParam(WorkItemQueryParam.TaskId, workItemId)
    },
    [setQueryParam]
  )

  const selectedWorkItemId = React.useMemo(
    () => location.queryParams[WorkItemQueryParam.TaskId] || undefined,
    [location.queryParams]
  )

  return { selectedWorkItemId, setSelectedWorkItemId }
}

function useSetWorkItemQueryParam() {
  const { history, location } = useRouter()
  const generatePath = useFrontendPathGenerator()

  return React.useCallback(
    (param: WorkItemQueryParam, value: string | number | undefined) => {
      const params = value ? { [param]: value } : undefined
      const exclude = value ? undefined : [param]
      history.push(generatePath(routes[location.name], params, "includeAllQueryParams", exclude))
    },
    [generatePath, history, location.name]
  )
}
